// src/components/admin/common/AdminActions.tsx
"use client";

import React from "react";
import { Trash2, Flag, MessageSquare, Ban, CheckCircle } from "lucide-react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useToast } from "@/components/ui/toast";
import { adminFetch } from "@/lib/adminFetch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export type AdminActionType = "delete" | "block" | "flag" | "approve" | "reject" | "note";

export type AdminActionConfig = {
  type: AdminActionType;
  label?: string;
  icon?: React.ReactNode;
  variant?: "default" | "destructive" | "outline";
  requiresNote?: boolean;
  confirmTitle?: string;
  confirmMessage?: string;
};

export type AdminActionResult = {
  success: boolean;
  error?: string;
};

interface AdminActionsProps<T = any> {
  item: T;
  itemType: "video" | "gallery" | "post" | "image"; // Type of content
  itemId: number | string;
  itemName?: string; // Display name for confirm dialogs
  actions: AdminActionType[]; // Which actions to show
  customActions?: AdminActionConfig[]; // Custom action configs
  onActionComplete?: (action: AdminActionType, result: AdminActionResult) => void;
  // API endpoint patterns - use {id} placeholder
  endpoints?: {
    delete?: string;
    block?: string;
    flag?: string;
    approve?: string;
    reject?: string;
    note?: string;
  };
}

const defaultActionConfigs: Record<AdminActionType, AdminActionConfig> = {
  delete: {
    type: "delete",
    label: "Delete",
    icon: <Trash2 className="h-4 w-4" />,
    variant: "destructive",
    confirmTitle: "Delete Item",
    confirmMessage: "This action cannot be undone.",
    requiresNote: true,
  },
  block: {
    type: "block",
    label: "Block",
    icon: <Ban className="h-4 w-4" />,
    variant: "destructive",
    confirmTitle: "Block Item",
    requiresNote: true,
  },
  flag: {
    type: "flag",
    label: "Flag",
    icon: <Flag className="h-4 w-4" />,
    variant: "outline",
    requiresNote: true,
  },
  approve: {
    type: "approve",
    label: "Approve",
    icon: <CheckCircle className="h-4 w-4" />,
    variant: "default",
  },
  reject: {
    type: "reject",
    label: "Reject",
    icon: <Ban className="h-4 w-4" />,
    variant: "destructive",
    requiresNote: true,
  },
  note: {
    type: "note",
    label: "Add Note",
    icon: <MessageSquare className="h-4 w-4" />,
    variant: "outline",
    requiresNote: true,
  },
};

export function AdminActions<T = any>({
  item,
  itemType,
  itemId,
  itemName,
  actions,
  customActions,
  onActionComplete,
  endpoints,
}: AdminActionsProps<T>) {
  const confirmDlg = useConfirm();
  const { toast } = useToast();
  const [busy, setBusy] = React.useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = React.useState(false);
  const [noteAction, setNoteAction] = React.useState<AdminActionType | null>(null);
  const [note, setNote] = React.useState("");

  // Default endpoint patterns
  const defaultEndpoints = {
    delete: `/api/proxy/api/${itemType}/v2/${itemId}`,
    block: `/api/proxy/api/${itemType}/v2/${itemId}/block`,
    flag: `/api/proxy/api/${itemType}/v2/${itemId}/flag`,
    approve: `/api/proxy/api/${itemType}/v2/${itemId}/approve`,
    reject: `/api/proxy/api/${itemType}/v2/${itemId}/reject`,
    note: `/api/proxy/api/${itemType}/v2/${itemId}/note`,
  };

  const resolvedEndpoints = { ...defaultEndpoints, ...endpoints };

  const getActionConfig = (actionType: AdminActionType): AdminActionConfig => {
    const custom = customActions?.find((a) => a.type === actionType);
    return { ...defaultActionConfigs[actionType], ...custom };
  };

  const executeAction = async (actionType: AdminActionType, noteText?: string) => {
    setBusy(true);
    const config = getActionConfig(actionType);

    try {
      const endpoint = resolvedEndpoints[actionType];
      if (!endpoint) {
        throw new Error(`No endpoint configured for action: ${actionType}`);
      }

      const method = actionType === "delete" ? "DELETE" : "POST";
      const body = noteText ? { reason: noteText, note: noteText } : undefined;

      // Pass the object through — adminFetch JSON.stringifies plain-object
      // bodies itself; pre-stringifying double-encoded the reason payload.
      // The reason ALSO goes out as the X-Reason header: the admin endpoints
      // ignore request bodies, and the API's audit trail reads X-Reason.
      await adminFetch(endpoint as any, {
        method,
        ...(actionType === "delete" && { confirmWord: "DELETE" }),
        ...(noteText && { reason: noteText }),
        ...(body && { body }),
      });

      toast({
        title: `${config.label || actionType} successful`,
        description: `${itemType} #${itemId} ${config.label?.toLowerCase() || actionType}d`,
        variant: "success",
      });

      onActionComplete?.(actionType, { success: true });
    } catch (error: any) {
      const errorMsg = error?.message || `Failed to ${actionType}`;
      toast({
        title: `${config.label || actionType} failed`,
        description: errorMsg,
        variant: "destructive",
      });
      onActionComplete?.(actionType, { success: false, error: errorMsg });
    } finally {
      setBusy(false);
    }
  };

  const handleAction = async (actionType: AdminActionType) => {
    const config = getActionConfig(actionType);

    // If action requires a note, open note dialog
    if (config.requiresNote) {
      setNoteAction(actionType);
      setNote("");
      setNoteDialogOpen(true);
      return;
    }

    // Otherwise, show confirm dialog if configured
    if (config.confirmTitle) {
      const displayName = itemName || `${itemType} #${itemId}`;
      const ok = await confirmDlg({
        title: config.confirmTitle,
        message: `${displayName}\n\n${config.confirmMessage || ""}`,
        confirmLabel: config.label || "Confirm",
        confirmType: config.variant === "destructive" ? "danger" : "default",
      });

      if (!ok) return;
    }

    await executeAction(actionType);
  };

  const handleNoteSubmit = async () => {
    if (!noteAction) return;
    if (!note.trim()) {
      toast({
        title: "Note required",
        description: "Please provide a reason for this action",
        variant: "destructive",
      });
      return;
    }

    setNoteDialogOpen(false);
    await executeAction(noteAction, note.trim());
    setNoteAction(null);
    setNote("");
  };

  return (
    <>
      <div className="flex items-center gap-2">
        {actions.map((actionType) => {
          const config = getActionConfig(actionType);
          return (
            <Button
              key={actionType}
              size="sm"
              variant={config.variant || "outline"}
              onClick={() => handleAction(actionType)}
              disabled={busy}
              className="gap-1"
            >
              {config.icon}
              {config.label || actionType}
            </Button>
          );
        })}
      </div>

      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {noteAction && getActionConfig(noteAction).label} - Add Reason
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label htmlFor="admin-note" className="block text-sm font-medium mb-2">
              Reason / Note
            </label>
            <Textarea
              id="admin-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter reason for this action..."
              rows={4}
              className="w-full"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialogOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={handleNoteSubmit} disabled={busy}>
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AdminActions;
